import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";

const AuditEventDetail = ({ event, onClose }) => {
  const [showRawJson, setShowRawJson] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const getActionColor = (action) => {
    const actionLower = action?.toLowerCase() || "";
    switch (actionLower) {
      case "create":
        return "success";
      case "delete":
        return "error";
      case "distributed":
        return "primary";
      case "update":
        return "warning";
      default:
        return "primary";
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString("en-GB", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const parseJsonSafe = (jsonString) => {
    if (!jsonString) return null;
    try {
      return JSON.parse(jsonString);
    } catch {
      return jsonString;
    }
  };

  const renderJsonValue = (value, depth = 0) => {
    if (value === null || value === undefined) {
      return <span className="text-muted-foreground italic">null</span>;
    }

    if (typeof value === "boolean") {
      return (
        <span className={value ? "text-success" : "text-error"}>
          {value.toString()}
        </span>
      );
    }

    if (typeof value === "number") {
      return <span className="text-primary">{value}</span>;
    }

    if (typeof value === "string") {
      // Check if it looks like a date
      if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
        try {
          return (
            <span className="text-secondary">
              {new Date(value).toLocaleString("en-GB")}
            </span>
          );
        } catch {
          return <span className="text-foreground">"{value}"</span>;
        }
      }
      // Check if it looks like a GUID
      if (
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          value
        )
      ) {
        return (
          <span className="text-muted-foreground font-mono text-xs">
            {value.substring(0, 8)}...
          </span>
        );
      }
      return <span className="text-foreground">"{value}"</span>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0)
        return <span className="text-muted-foreground">[]</span>;
      return (
        <div className="ml-4">
          {value.map((item, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <span className="text-muted-foreground">[{idx}]:</span>
              {renderJsonValue(item, depth + 1)}
            </div>
          ))}
        </div>
      );
    }

    if (typeof value === "object") {
      return (
        <div className={depth > 0 ? "ml-4" : ""}>
          {Object.entries(value).map(([key, val]) => (
            <div key={key} className="flex items-start gap-2 py-0.5">
              <span className="text-primary font-medium">{key}:</span>
              {renderJsonValue(val, depth + 1)}
            </div>
          ))}
        </div>
      );
    }

    return <span>{String(value)}</span>;
  };

  const renderChangedProperties = () => {
    const changes = parseJsonSafe(event?.changedProperties);
    if (!changes) return null;

    return (
      <div className="space-y-2">
        {Object.entries(changes).map(([property, change]) => (
          <div key={property} className="p-3 bg-muted/50 rounded-lg">
            <p className="text-xs font-semibold text-foreground mb-2">
              {property}
            </p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-muted-foreground block mb-0.5">Before:</span>
                <span className="text-error">
                  {JSON.stringify(change?.Old ?? change?.old)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground block mb-0.5">After:</span>
                <span className="text-success">
                  {JSON.stringify(change?.New ?? change?.new)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: "Info" },
    { id: "changes", label: "Changes", icon: "GitCompare" },
    { id: "raw", label: "Raw Data", icon: "Code" },
  ];

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden sticky top-6">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h2 className="text-lg font-heading font-semibold text-foreground">
            Event Details
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-${getActionColor(
              event?.eventType
            )}/10 text-${getActionColor(event?.eventType)} border border-${getActionColor(
              event?.eventType
            )}/20`}
          >
            {event?.eventType?.charAt(0).toUpperCase() +
              event?.eventType?.slice(1)}
          </span>
          <span className="text-xs text-muted-foreground">
            {event?.eventCategory}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-3 py-2 text-xs font-medium transition-colors flex items-center justify-center gap-1.5 ${
              activeTab === tab.id
                ? "text-primary border-b-2 border-primary bg-primary/5"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            }`}
          >
            <Icon name={tab.icon} size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
        {activeTab === "overview" && (
          <>
            {/* Title and Description */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-1">
                {event?.title}
              </h3>
              <p className="text-xs text-muted-foreground">
                {event?.description}
              </p>
            </div>

            {/* Entity Info */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-xs font-semibold text-muted-foreground mb-2">
                ENTITY
              </p>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-xs text-muted-foreground">Type</span>
                  <span className="text-xs font-medium">
                    {event?.eventCategory}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-muted-foreground">ID</span>
                  <span className="text-xs font-mono text-muted-foreground">
                    {event?.entityId}
                  </span>
                </div>
                {event?.source && (
                  <div className="flex justify-between">
                    <span className="text-xs text-muted-foreground">Source</span>
                    <span className="text-xs font-medium">{event?.source}</span>
                  </div>
                )}
              </div>
            </div>

            {/* User Information */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-xs font-semibold text-muted-foreground mb-2">
                PERFORMED BY
              </p>
              <div className="flex items-center gap-3">
                <Image
                  src={event?.user?.avatar}
                  alt={event?.user?.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">
                    {event?.user?.name}
                  </p>
                </div>
              </div>
            </div>

            {/* Timestamp */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-xs font-semibold text-muted-foreground mb-2">
                TIMESTAMP
              </p>
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={14} className="text-primary" />
                <p className="text-xs text-foreground">
                  {formatTimestamp(event?.timestamp)}
                </p>
              </div>
            </div>

            {/* Correlation ID */}
            {event?.correlationId && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs font-semibold text-muted-foreground mb-2">
                  CORRELATION ID
                </p>
                <p className="text-xs font-mono text-muted-foreground break-all">
                  {event?.correlationId}
                </p>
              </div>
            )}
          </>
        )}

        {activeTab === "changes" && (
          <>
            {event?.changedProperties ? (
              <>
                <p className="text-xs font-semibold text-muted-foreground mb-2">
                  CHANGED PROPERTIES
                </p>
                {renderChangedProperties()}
              </>
            ) : event?.oldValues || event?.newValues ? (
              <div className="space-y-4">
                {event?.oldValues && (
                  <div>
                    <p className="text-xs font-semibold text-error mb-2">
                      BEFORE
                    </p>
                    <div className="p-3 bg-error/5 rounded-lg border border-error/20 text-xs overflow-x-auto">
                      {renderJsonValue(parseJsonSafe(event.oldValues))}
                    </div>
                  </div>
                )}
                {event?.newValues && (
                  <div>
                    <p className="text-xs font-semibold text-success mb-2">
                      AFTER
                    </p>
                    <div className="p-3 bg-success/5 rounded-lg border border-success/20 text-xs overflow-x-auto">
                      {renderJsonValue(parseJsonSafe(event.newValues))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Icon
                  name="FileText"
                  size={32}
                  className="mx-auto mb-2 text-muted-foreground"
                />
                <p className="text-xs text-muted-foreground">
                  No change details available
                </p>
              </div>
            )}
          </>
        )}

        {activeTab === "raw" && (
          <div>
            <div className="flex justify-between items-center mb-2">
              <p className="text-xs font-semibold text-muted-foreground">
                RAW EVENT DATA
              </p>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(event, null, 2));
                }}
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                <Icon name="Copy" size={12} />
                Copy
              </button>
            </div>
            <pre className="p-3 bg-muted rounded-lg text-xs overflow-x-auto max-h-[400px] overflow-y-auto font-mono">
              {JSON.stringify(event, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="p-4 border-t border-border">
        <Button
          variant="outline"
          size="sm"
          fullWidth
          iconName="Download"
          iconPosition="left"
          onClick={() => {
            const eventData = JSON.stringify(event, null, 2);
            const blob = new Blob([eventData], { type: "application/json" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `audit-event-${event?.id}.json`;
            a.click();
          }}
        >
          Export Event
        </Button>
      </div>
    </div>
  );
};

export default AuditEventDetail;
